"use client";

import type { ContextAttributes } from "@/utils/context/attributes";
import {
  getContextAttributes,
  pushTraceScope,
  type TraceScopeHandle,
} from "@/utils/tracing/client";
import { datadogLogs } from "@datadog/browser-logs";
import * as Sentry from "@sentry/nextjs";
import { useEffect, useRef } from "react";

// What the sinks currently hold, so a scope change only touches the keys
// whose effective value moved.
let appliedAttributes: Record<string, string> = {};

const syncSinks = (): void => {
  const next = getContextAttributes();
  Object.keys(appliedAttributes)
    .filter((key) => !(key in next))
    .forEach((key) => {
      Sentry.setTag(key, undefined);
      datadogLogs.removeGlobalContextProperty(key);
    });
  Object.entries(next)
    .filter(([key, value]) => appliedAttributes[key] !== value)
    .forEach(([key, value]) => {
      Sentry.setTag(key, value);
      datadogLogs.setGlobalContextProperty(key, value);
    });
  appliedAttributes = next;
};

// Holds the given IDs in the browser trace scope, Sentry tags, and Datadog
// browser log context while mounted. Scopes nest: unmounting an inner one
// restores the outer values. Use the hook in components with early returns.
export const useTraceScope = ({
  studyId,
  organizationId,
  userId,
  responseId,
}: ContextAttributes): void => {
  const scopeRef = useRef<TraceScopeHandle | null>(null);

  // Pushed once per mount so an outer scope whose IDs change (the workspace
  // user hydrating) keeps its position under still-mounted inner scopes.
  useEffect(() => {
    const scope = pushTraceScope();
    scopeRef.current = scope;
    return () => {
      scope.pop();
      scopeRef.current = null;
      syncSinks();
    };
  }, []);

  useEffect(() => {
    scopeRef.current?.update({ studyId, organizationId, userId, responseId });
    syncSinks();
  }, [studyId, organizationId, userId, responseId]);
};

export function TraceScope(attributes: ContextAttributes) {
  useTraceScope(attributes);
  return null;
}
